import React, { useEffect, useMemo, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { Card, Table, Row, Col, Form, Button, InputGroup } from "react-bootstrap";
import SearchIcon from "@mui/icons-material/Search";

import BreadCrumbComponent from "../../components/BreadCrumbComponent";
import CurrentDateComponents from "../../components/CurrentDateComponents";
import AnimationLoading from "../../components/Animationloading";
import EmptyComponent from "../../components/EmptyComponent";
import { numberFormat, NewCovertTimeZone, covertTimeZone } from "../../helper";
import { getStartDate, getEndDate } from "../../components/handleDate";
import { GET_REPORT_COMMISSIONS } from "../../apollo/report/query";
import { GET_USERS } from "../../apollo/user/query";
import { SHOP_SETTING_KEY, USER_KEY } from "../../const";

export default function ReportCommission() {
	const shopId = JSON.parse(localStorage.getItem("SHOP_DATA"))?.id;
	const userId = JSON.parse(localStorage.getItem(USER_KEY))?.data?.id;
	const shopSetting = JSON.parse(localStorage.getItem(SHOP_SETTING_KEY)) || {};
	const commissionEnable = shopSetting?.commissionEnable ?? false;
	const [activeButton, setActiveButton] = useState("thisMonth");
	const [filter, setFilter] = useState({
		createdAt_gte: getStartDate("thisMonth"),
		createdAt_lt: getEndDate("thisMonth"),
	});
	const [isCurrentModals, setIsCurrentModals] = useState(false);
	const [searchText, setSearchText] = useState("");
	const [selectedPlatform, setSelectedPlatform] = useState("");
	const [createdBy, setCreatedBy] = useState(userId || "");
	const [employees, setEmployees] = useState([]);

	const [loadReport, { data: reportData, loading: reportLoading, error: reportError }] =
		useLazyQuery(GET_REPORT_COMMISSIONS, { fetchPolicy: "cache-and-network" });
	const [loadEmployees, { data: employeeData }] = useLazyQuery(GET_USERS, {
		fetchPolicy: "cache-and-network",
	});

	useEffect(() => {
		const fetchEmployees = async () => {
			if (!shopId) return;
			await loadEmployees({
				variables: {
					where: { shop: shopId, isDeleted: false },
					orderBy: "createdAt_DESC",
					limit: 200,
				},
			});
		};
		fetchEmployees();
	}, [shopId, loadEmployees]);

	useEffect(() => {
		if (employeeData?.users?.data) {
			setEmployees(employeeData.users.data);
		}
	}, [employeeData]);

	useEffect(() => {
		const fetchReport = async () => {
			if (!shopId) return;
			let where = { shop: shopId, createdBy: createdBy || userId };
			if (filter?.createdAt_gte && filter?.createdAt_lt) {
				where = {
					...where,
					createdAt_gte: NewCovertTimeZone(filter?.createdAt_gte),
					createdAt_lt: NewCovertTimeZone(filter?.createdAt_lt),
				};
			}
			if (selectedPlatform) {
				where = { ...where, commissionPlatform: selectedPlatform };
			}
			await loadReport({ variables: { where } });
		};
		fetchReport();
	}, [shopId, userId, filter?.createdAt_gte, filter?.createdAt_lt, selectedPlatform, createdBy, loadReport]);

	const summary = useMemo(() => reportData?.reportCommission?.summary || {}, [reportData]);
	const rows = useMemo(() => reportData?.reportCommission?.data || [], [reportData]);

	const filteredRows = useMemo(() => {
		if (!searchText) return rows;
		const q = searchText.toLowerCase();
		return rows.filter((row) => {
			const orderGroupCode = (row?.orderGroupCode || "").toLowerCase();
			const platform = (row?.commissionPlatform || "").toLowerCase();
			const createdByName = `${row?.createdBy?.firstName || ""} ${row?.createdBy?.lastName || ""} ${row?.createdBy?.nickname || ""}`
				.toLowerCase();
			return orderGroupCode.includes(q) || createdByName.includes(q) || platform.includes(q);
		});
	}, [rows, searchText]);

	if (!commissionEnable) {
		return (
			<div style={{ padding: 10 }}>
				<BreadCrumbComponent txtSecond={"ລາຍງານຄ່າຄອມມິດຊັ່ນ"} />
				<div style={{ padding: "0 1em", textAlign: 'center', marginTop: '2rem' }}>
					<h4><b>ບໍ່ສາມາດເຂົ້າເຖິງລາຍງານຄ່າຄອມມິດຊັ່ນໄດ້</b></h4>
					<p>ກະລຸນາເປີດໃຊ້ງານ "ຄິດໄລ່ຄ່າຄອມມິດຊັ່ນ" ໃນສ່ວນຂອງການຕັ້ງຄ່າຮ້ານກ່ອນ.</p>
				</div>
			</div>
		);
	}

	return (
		<div style={{ padding: 10 }}>
			<BreadCrumbComponent txtSecond={"ລາຍງານຄ່າຄອມມິດຊັ່ນ"} />
			<div style={{ padding: "0 1em" }}>
				<h4><b>ລາຍງານຄ່າຄອມມິດຊັ່ນ</b></h4>
				<p>ສະຫຼຸບຄ່າຄອມມິດຊັ່ນຕາມຊ່ວງເວລາ</p>
				<Row className="mb-3">
					<Col md={3}>
						<Form.Label htmlFor="search-input">ຄົ້ນຫາ</Form.Label>
						<InputGroup>
							<InputGroup.Text style={{ backgroundColor: "#FFFFFF" }}>
								<SearchIcon sx={{ color: "#6B7280" }} />
							</InputGroup.Text>
							<Form.Control
								id="search-input"
								onChange={(e) => setSearchText(e?.target?.value)}
								value={searchText}
								placeholder="ຄົ້ນຫາດ້ວຍ OrderGroup ID/Platform"
							/>
						</InputGroup>
					</Col>
					<Col md={3}>
						<Form.Label>Platform</Form.Label>
						<Form.Select
							value={selectedPlatform}
							onChange={(e) => setSelectedPlatform(e.target.value)}
						>
							<option value="">--ທັງໝົດ--</option>
							<option value="POS">POS</option>
							<option value="ONLINE">ONLINE</option>
						</Form.Select>
					</Col>
					<Col md={3}>
						<Form.Label>ພະນັກງານ</Form.Label>
						<Form.Select
							value={createdBy || userId || ""}
							onChange={(e) => setCreatedBy(e.target.value)}
						>
							<option value={userId || ""}>--ເລືອກ--</option>
							{employees.map((employee) => (
								<option key={employee?.id} value={employee?.id}>
									{`${employee?.firstName || ""} ${employee?.lastName || ""}`.trim() || employee?.nickname || employee?.username || employee?.phone}
								</option>
							))}
						</Form.Select>
					</Col>
					<Col md={3}>
						<Form.Label htmlFor="date-range">ເລືອກວັນທີ</Form.Label>
						<Button
							id="date-range"
							variant="outline-secondary"
							onClick={() => setIsCurrentModals(true)}
							style={{ width: "280px", display: "flex", gap: 5, justifyContent: "space-between", alignItems: "center" }}
						>
							{!filter ? (
								"mm/dd/yyyy"
							) : (
								<>
									{covertTimeZone(filter?.createdAt_gte) + " ຫາ " + covertTimeZone(filter?.createdAt_lt)}
								</>
							)}
						</Button>
					</Col>
				</Row>
			</div>

			<Row className="mb-3 px-3">
				<Col md={4}>
					<Card className="shadow-sm">
						<Card.Body>
							<div className="d-flex justify-content-between align-items-center">
								<div>
									<div>ຈຳນວນລາຍການ</div>
									<div style={{ fontSize: 22, fontWeight: "bold" }}>
										{numberFormat(summary?.totalCount || 0)}
									</div>
								</div>
							</div>
						</Card.Body>
					</Card>
				</Col>
				<Col md={4}>
					<Card className="shadow-sm">
						<Card.Body>
							<div className="d-flex justify-content-between align-items-center">
								<div>
									<div>ຍອດຄ່າຄອມມິດຊັ່ນ</div>
									<div style={{ fontSize: 22, fontWeight: "bold" }}>
										{numberFormat(summary?.totalCommissionValue || 0)}
									</div>
								</div>
							</div>
						</Card.Body>
					</Card>
				</Col>
			</Row>

			<Card>
				<Card.Header>ລາຍການຄ່າຄອມມິດຊັ່ນ</Card.Header>
				<Card.Body>
					{reportLoading && <AnimationLoading />}
					{reportError && <div className="text-danger">ເກີດຂໍ້ຜິດພາດໃນການດຶງຂໍ້ມູນ</div>}
					{!reportLoading && filteredRows?.length === 0 && <EmptyComponent />}

					{!reportLoading && filteredRows?.length > 0 && (
						<div style={{ overflowX: "auto" }}>
							<Table striped bordered hover size="sm">
								<thead>
									<tr>
										<th>ລຳດັບ</th>
										<th>ລະຫັດອໍເດີ</th>
										<th>ພະນັກງານ</th>
										<th>ຊ່ອງທາງການຂາຍ</th>
										<th>ຄອມມິດຊັ່ນ</th>
										<th>ວັນທີ</th>
									</tr>
								</thead>
								<tbody>
									{filteredRows.map((row, idx) => (
										<tr key={`${row?.orderGroupId || "row"}-${idx}`}>
											<td>{idx + 1}</td>
											<td>{row?.orderGroupCode || "-"}</td>
											<td>
												{`${row?.createdBy?.firstName || ""} ${row?.createdBy?.lastName || ""}`.trim() || row?.createdBy?.nickname || "-"}
											</td>
											<td>{row?.commissionPlatform || "-"}</td>
											<td>{numberFormat(row?.commissionValue || 0)}</td>
											<td>{row?.createdAt ? covertTimeZone(row?.createdAt, "HH:mm:ss") : "-"}</td>
										</tr>
									))}
								</tbody>
							</Table>
						</div>
					)}
				</Card.Body>
			</Card>

			<CurrentDateComponents
				onCloseModal={() => setIsCurrentModals(false)}
				openModal={isCurrentModals}
				setCdFillter={setFilter}
				setActiveButton={setActiveButton}
				activeButton={activeButton}
			/>
		</div>
	);
}